/** Pure attribution helpers. No DOM, so they can be checked without the app. */

export const SESSION_IDLE_MS = 30 * 60 * 1000

const CAMPAIGN_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'fbclid',
    'gclid',
    'r',
] as const

export interface Touch {
    utm_source: string | null
    utm_medium: string | null
    utm_campaign: string | null
    utm_term: string | null
    utm_content: string | null
    fbclid: string | null
    gclid: string | null
    r: string | null
    referrer: string | null
    landing_path: string | null
    device: string | null
    captured_at: string
}

export function normalizePath(path: string): string {
    const bare = (path || '/').split('?')[0].split('#')[0] || '/'
    if (bare.length > 1 && bare.endsWith('/')) return bare.slice(0, -1)
    return bare || '/'
}

export function deviceFromUserAgent(ua: string): 'mobile' | 'tablet' | 'desktop' {
    if (/iPad|Tablet|PlayBook/i.test(ua)) return 'tablet'
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile'
    return 'desktop'
}

export function touchHasCampaign(touch: Partial<Touch> | null | undefined): boolean {
    if (!touch) return false
    return CAMPAIGN_PARAMS.some((key) => Boolean(touch[key]))
}

export function touchFromSearch(input: {
    search: string
    referrer: string
    path: string
    device: string
    now?: string
    sameOrigin?: string
}): Touch {
    const params = new URLSearchParams(input.search.startsWith('?') ? input.search.slice(1) : input.search)
    const read = (key: string) => {
        const value = params.get(key)
        if (!value) return null
        const trimmed = value.trim()
        if (!trimmed || trimmed.length > 500) return null
        return trimmed
    }
    let referrer = input.referrer.trim() || null
    if (referrer && input.sameOrigin && referrer.startsWith(input.sameOrigin)) referrer = null
    return {
        utm_source: read('utm_source'),
        utm_medium: read('utm_medium'),
        utm_campaign: read('utm_campaign'),
        utm_term: read('utm_term'),
        utm_content: read('utm_content'),
        fbclid: read('fbclid'),
        gclid: read('gclid'),
        r: read('r'),
        referrer,
        landing_path: normalizePath(input.path),
        device: input.device,
        captured_at: input.now || new Date().toISOString(),
    }
}

export function applyTouch(first: Touch | null, last: Touch | null, incoming: Touch): { first: Touch, last: Touch } {
    if (!first) return { first: incoming, last: incoming }
    if (touchHasCampaign(incoming)) return { first, last: incoming }
    return { first, last: last ?? first }
}

export function nextSessionId(
    previousId: string | null,
    lastSeenMs: number | null,
    nowMs: number,
    createId: () => string,
): { id: string, rotated: boolean } {
    if (!previousId || lastSeenMs == null || Number.isNaN(lastSeenMs)) {
        return { id: createId(), rotated: !previousId }
    }
    if (nowMs - lastSeenMs > SESSION_IDLE_MS) {
        return { id: createId(), rotated: true }
    }
    return { id: previousId, rotated: false }
}

/** New accounts only. Pending covers email confirmation after the 5-minute window. */
export function shouldStitchIdentity(
    createdAt: string | null | undefined,
    pendingAt: string | null,
    now = Date.now(),
): boolean {
    if (!createdAt) return false
    const created = Date.parse(createdAt)
    if (!Number.isFinite(created)) return false
    const age = now - created
    const day = 24 * 60 * 60 * 1000
    if (age < -60_000 || age > day) return false
    if (age <= 5 * 60 * 1000) return true
    if (!pendingAt) return false
    const pending = Date.parse(pendingAt)
    if (!Number.isFinite(pending)) return false
    return now - pending <= day && pending >= created - 60_000
}
